import type { Object3D } from 'three'
import { createVisibleModelExportRoot } from './createVisibleModelExportRoot'
import { normalizePuppyExportFileBaseName } from './puppyPackageUtils'
import { canonicalizePuppyGlbBytes } from './canonicalizePuppyGlbBytes'
import type {
  ExportGlbOptions,
  PuppyBinaryExportResult,
  PuppyVisibleModelExportRoot,
} from './types'

/**
 * 将 GLTFExporter 结果规整为 GLB 字节。
 *
 * @param result GLTFExporter 回调结果。
 * @returns GLB 字节。
 */
function normalizeGltfExporterResult(result: ArrayBuffer | object): Uint8Array {
  if (result instanceof ArrayBuffer) {
    return canonicalizePuppyGlbBytes(new Uint8Array(result))
  }

  throw new Error('GLB 导出期望 GLTFExporter 返回 ArrayBuffer，但当前返回了 JSON 对象')
}

/**
 * 直接把传入的 Three.js 节点及其全部后代编码为 GLB。
 *
 * 设计说明：
 * 1. 本函数不创建 PuppyCAD 可见模型快照，也不执行拓扑代理、辅助对象或交互对象过滤；
 * 2. `onlyVisible=false` 时会把隐藏节点一并交给 GLTFExporter，服务于原始场景调试快照；
 * 3. 正式产品 GLB 导出仍应先通过 `createVisibleModelExportRoot()` 建立干净快照。
 *
 * @param sceneObject 需要直接导出的 Three.js 根节点。
 * @param onlyVisible 是否只导出可见节点。
 * @returns GLB 字节。
 */
export async function exportSceneObjectToGlbBytes(
  sceneObject: Object3D,
  onlyVisible: boolean,
): Promise<Uint8Array> {
  const { GLTFExporter } = await import('three/examples/jsm/exporters/GLTFExporter.js')
  return new Promise((resolve, reject) => {
    const exporter = new GLTFExporter()
    exporter.parse(
      sceneObject,
      (result) => {
        try {
          resolve(normalizeGltfExporterResult(result as ArrayBuffer | object))
        } catch (error) {
          reject(error)
        }
      },
      (error) => {
        reject(error instanceof Error ? error : new Error(String(error)))
      },
      {
        binary: true,
        onlyVisible,
      },
    )
  })
}

/**
 * 使用 threejs GLTFExporter 导出二进制 GLB。
 *
 * @param exportRoot 可见模型导出根。
 * @returns GLB 字节。
 * @throws 当导出根中没有可导出的可见模型时。
 */
export async function exportVisibleModelRootToGlbBytes(exportRoot: PuppyVisibleModelExportRoot): Promise<Uint8Array> {
  if (exportRoot.diagnostics.exportedMeshCount <= 0) {
    throw new Error('当前场景没有可导出的可见模型')
  }

  return exportSceneObjectToGlbBytes(exportRoot.root, true)
}

/**
 * 导出当前可见模型为 GLB。
 *
 * @param options GLB 导出选项。
 * @returns GLB 文件结果。
 */
export async function exportGlb(options: ExportGlbOptions): Promise<PuppyBinaryExportResult> {
  const exportRoot = createVisibleModelExportRoot(options.sceneRoot)
  const bytes = await exportVisibleModelRootToGlbBytes(exportRoot)

  return {
    fileName: `${normalizePuppyExportFileBaseName(options.fileBaseName)}.glb`,
    mimeType: 'model/gltf-binary',
    bytes,
  }
}
